/**
 * Debug logging utility
 * Footer debug toggle enables `debugLog` (API timing, general diagnostics).
 * Verbose traces (render loops, entity loader steps) use `debugVerboseLog`.
 * MSAL config dumps use REACT_APP_MSAL_DIAG=true only.
 */

let debugEnabled = false;
let verboseDebugEnabled = false;

const VERBOSE_STORAGE_KEY = 'api_debug_verbose';

function readVerboseFromStorage(): boolean {
    try {
        return sessionStorage.getItem(VERBOSE_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

/**
 * Enable debug logging (footer toggle)
 */
export function enableDebugLogging(): void {
    debugEnabled = true;
    verboseDebugEnabled = readVerboseFromStorage();
}

/**
 * Disable debug logging
 */
export function disableDebugLogging(): void {
    debugEnabled = false;
    verboseDebugEnabled = false;
}

/**
 * Check if debug logging is enabled
 */
export function isDebugLoggingEnabled(): boolean {
    return debugEnabled;
}

export function isVerboseDebugEnabled(): boolean {
    return debugEnabled && verboseDebugEnabled;
}

export function setVerboseDebugLogging(enabled: boolean): void {
    verboseDebugEnabled = enabled;
    try {
        if (enabled) {
            sessionStorage.setItem(VERBOSE_STORAGE_KEY, 'true');
        } else {
            sessionStorage.removeItem(VERBOSE_STORAGE_KEY);
        }
    } catch {
        // ignore
    }
}

/** MSAL config dumps — separate from footer debug (very noisy). */
export function isMsalDiagEnabled(): boolean {
    return process.env.REACT_APP_MSAL_DIAG === 'true';
}

/**
 * Debug log - only logs if debug mode is enabled
 */
export function debugLog(...args: any[]): void {
    if (debugEnabled) {
        console.log(...args);
    }
}

/**
 * Verbose debug (entity loader steps, render traces) — debug on + verbose flag or env.
 */
export function debugVerboseLog(...args: any[]): void {
    if (debugEnabled && verboseDebugEnabled) {
        console.log(...args);
    }
}

/**
 * Stringify an object for data-notes attributes, omitting properties whose value is false or undefined to reduce noise.
 */
export function stringifyDebugNotes(obj: Record<string, unknown>): string {
    const filtered = Object.fromEntries(
        Object.entries(obj).filter(([, v]) => v !== false && v !== undefined)
    );
    return JSON.stringify(filtered);
}
